import React, { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMagnifyingGlass,
  faUserPlus,
  faList,
} from '@fortawesome/free-solid-svg-icons';
import {
  faFile,
} from '@fortawesome/free-regular-svg-icons';
import {
  faSignsPost,
  faArrowRightFromBracket,
} from '@fortawesome/pro-regular-svg-icons';

import AuthContext from '../contexts/AuthContext';
import { buttonBackground } from '../styles/colors';
import logo from '../Logo.png';

const MenuContainer = styled.div`
  height: 100%;
  min-height: 100vh;
  box-shadow: 0px 4px 50px rgba(0, 36, 64, 0.12);
  width: 225px;
  min-width: 225px;
  display: flex;
  flex-direction: column;
  padding-left: 36px;
  padding-right: 36px;

  @media (max-width: 768px) {
    flex-direction: row;
    height: 108px;
    min-height: 108px;
    width: 100%;
    min-width: 100vw;
  }
`;

const LogoContainer = styled.div`
  text-align: center;
  margin-top: 20px;

  @media (max-width: 768px) {
    margin-top: 0px;
    text-align: left;
    flex: 1;
    align-self: center;
    max-width: 50%;
  }
`;

const Search = styled.button`
  margin-top: 20px;
  border: 0px;
  background-color: ${buttonBackground};
  border-radius: 6px;
  color: white;
  font-size: 1rem;
  padding: 6px;

  @media (max-width: 768px) {
    margin-top: 0;
    align-self: center;
    width: 260px;
    max-width: 50%;
    height: 36px;
    text-align: left;
    margin-left: 10px;
    padding-left: 10px;
  }
`;

const MenuLine = styled.div`
  width: 32px;
  height: 4px;
  border-radius: 2px;
  background-color: ${buttonBackground};
  margin: 6px 0;
`;

const MenuIcon = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: block;
    align-self: center;
    margin-left: 24px;

    .show & {
      margin-top: 4px;
      margin-bottom: 32px;
      margin-left: auto;
      margin-right: 0px;
    }
  }
`;

const Menu = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  border-top: 1px solid #dedfe0;
  margin-top: 10px;
  padding-top: 32px;

  @media (max-width: 768px) {
    display: none;
    position: fixed;
    right: 0;
    background-color: white;
    box-shadow: 0px 4px 50px rgba(0, 36, 64, 0.12);
    height: 100%;
    min-height: 100vh;
    margin: 0px;
    z-index: 9999;
    padding-left 36px;
    padding-right 36px;
    animation: fadeIn 500ms;

    &.show {
      display: flex;
    }
  }
`;

const LinkItemStyles = `
  display: block;
  margin-bottom: 12px;
  font-weight: 600;
  color: #111111;
  text-decoration: none;
`;

export const LinkItem = styled(Link)`
  ${LinkItemStyles}
`;

export const MailItem = styled.a`
  ${LinkItemStyles}
`;

export const Item = styled.div`
  margin-bottom: 12px;
  font-weight: 600;
  color: #111111;
`;

const InfoItem = styled(Item)`
  font-weight: 400;
  margin-bottom: 8px;
`;

const Footer = styled.div`
  margin-top: auto;
  margin-bottom: 36px;
`;

const Icon = styled(FontAwesomeIcon)`
  margin-right: 12px;
  font-weight: 500;
  color: ${buttonBackground};
`;

const FboDetails = styled.div`
  display: flex;
  justify-content: space-between;
  padding-top: 20px;
  @media (max-width: 768px) {
    flex-direction: column;
    justify-content: flex-start;
  }
`;

const Navigation = () => {
  const {
    clearCredentials,
    role,
    fboName,
    airportCode,
    setDataLoaded,
    setLoginAttempted
  } = useContext(AuthContext);
  const [showMenu, setShowMenu] = useState(false);
  const navigate = useNavigate();

  const home = () => {
    navigate('/');
  }

  return (
    <MenuContainer>
      <LogoContainer>
        <img
          src={logo}
          alt="Fly Louie"
          style={{width: 150, maxWidth: '100%'}}
          onClick={() => home()}
        />
      </LogoContainer>
      <FboDetails>
        <div>{fboName}</div><div>{airportCode}</div>
      </FboDetails>
      <Search onClick={() => home()}>
        <Icon icon={faMagnifyingGlass} style={{color: 'white'}}/>
        Search
      </Search>

      <MenuIcon onClick={() => setShowMenu(!showMenu)}>
        <MenuLine />
        <MenuLine />
        <MenuLine />
      </MenuIcon>

      <Menu className={showMenu ? 'show' : ''}>
        <MenuIcon onClick={() => setShowMenu(!showMenu)}>
          <MenuLine />
          <MenuLine />
          <MenuLine />
        </MenuIcon>

        <LinkItem to="/instructions" onClick={() => setShowMenu(false)}>
          <Icon icon={faSignsPost} />
          Instructions
        </LinkItem>

        {'Admin' === role && (
          <>
            <LinkItem to="/users/new" onClick={() => setShowMenu(false)}>
              <Icon icon={faUserPlus} style={{marginRight: 10}} />
              Add User
            </LinkItem>

            <LinkItem to="/users" onClick={() => setShowMenu(false)}>
              <Icon icon={faList} />
              User List
            </LinkItem>

            <LinkItem to="/statements" onClick={() => setShowMenu(false)}>
              <Icon icon={faFile} />
              Statements
            </LinkItem>
          </>
        )}

        <Item
          style={{marginBottom: 48, cursor: 'pointer'}}
          onClick={() => {
            setLoginAttempted(null);
            setDataLoaded(false);
            clearCredentials();
            navigate('/');
          }}
        >
          <Icon icon={faArrowRightFromBracket} />
          Logout
        </Item>

        <Footer>
          <LinkItem to="/assistance" onClick={() => setShowMenu(false)}>
            Contact Fly Louie
          </LinkItem>

          <MailItem href="mailto:fuel@flylouie.com">
            fuel@flylouie.com
          </MailItem>

          <InfoItem>833-FLY-LOUIE</InfoItem>
          <InfoItem>(833-359-5684)</InfoItem>
        </Footer>
      </Menu>
    </MenuContainer>
  );
}

export default Navigation;
